import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebase/firebase';
import { log } from '../services/production-logger';
import { Organization, OrganizationFormData, OrganizationMember, OrganizationMembership } from '../models/organization';
import { useAuth } from './auth-context';

// Lazy load services to prevent circular dependencies
let organizationService: any;
let organizationMembershipService: any;
let logEvent: any;

const getServices = () => {
  if (!organizationService) {
    organizationService = require('../services/organization-service').default;
    organizationMembershipService = require('../services/organization-membership-service').default;
    logEvent = require('../services/analytics-service').logEvent;
  }
  return { organizationService, organizationMembershipService, logEvent };
};
import { initManager } from '../utils/initialization-manager';

interface OrganizationContextType {
  organization: Organization | null;
  activeOrganization: Organization | null; // Alias for backward compatibility
  userMemberships: OrganizationMembership[];
  isLoading: boolean;
  error: string | null;
  members: OrganizationMember[];
  setActiveOrganization: (organizationId: string) => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>; // Alias for backward compatibility
  refreshOrganization: () => Promise<void>;
  createDefaultOrganization: () => Promise<Organization | null>;
  ensureOrganizationAccess: () => Promise<boolean>;
  updateOrganization: (data: Partial<OrganizationFormData>) => Promise<Organization | null>;
  loadOrganization: (id?: string) => Promise<void>;
  loadMembers: () => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
  getUserOrganizations: () => Promise<Organization[]>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

// Backward compatibility alias
export const useOrganizationContext = useOrganization;

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userMemberships, setUserMemberships] = useState<OrganizationMembership[]>([]);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Loading guards to prevent concurrent calls
  const loadingMembershipsRef = useRef(false);
  const loadingOrganizationRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);
  
  // Initialization guards to prevent stack overflow
  const initializationRef = useRef(false);
  const initTimeoutRef = useRef<NodeJS.Timeout>();
  const isInitializedRef = useRef(false);
  
  // Guard to prevent recursive setActiveOrganization calls
  const switchingOrganizationRef = useRef(false);

  /**
   * Load user's organization memberships with loading guard
   */
  const loadUserMemberships = useCallback(async (userId: string) => {
    // Prevent concurrent loads for the same user
    if (loadingMembershipsRef.current || lastUserIdRef.current === userId) {
      log.debug('🏢 Org: Skipping duplicate membership load for:', userId);
      return;
    }

    loadingMembershipsRef.current = true;
    lastUserIdRef.current = userId;

    try {
      log.debug('🏢 Org: Loading memberships for user:', userId);
      const { organizationMembershipService } = getServices();
      const memberships = await organizationMembershipService.getUserMemberships(userId);
      setUserMemberships(memberships);
      
      // Find primary membership or use first one
      const primaryMembership = memberships.find(m => m.isPrimary) || memberships[0];
      
      // Only auto-load organization if we're not in the middle of switching
      if (primaryMembership && !organization && !switchingOrganizationRef.current) {
        // Load the primary organization
        await loadOrganization(primaryMembership.organizationId);
      }
      
      log.debug('🏢 Org: Loaded', memberships.length, 'memberships');
    } catch (error) {
      log.error('🏢 Org: Error loading memberships:', error);
      setError('Failed to load organization memberships');
    } finally {
      loadingMembershipsRef.current = false;
    }
  }, [organization]);

  /**
   * Get all organizations for the current user
   */
  const getUserOrganizations = useCallback(async (): Promise<Organization[]> => {
    if (!currentUser) {
      log.debug('🏢 Org: No user, cannot get organizations');
      return [];
    }

    try {
      const organizations = await Promise.all(
        userMemberships.map(membership => 
          getServices().organizationService.getOrganizationById(membership.organizationId)
        )
      );
      
      return organizations.filter((org): org is Organization => org !== null);
    } catch (error) {
      log.error('🏢 Org: Error getting user organizations:', error);
      return [];
    }
  }, [currentUser, userMemberships]);

  /**
   * Create a default organization for the user
   */
  const createDefaultOrganization = useCallback(async (): Promise<Organization | null> => {
    if (!currentUser) {
      log.debug('🏢 Org: No user, cannot create default organization');
      return null;
    }

    try {
      log.debug('🏢 Org: Creating default organization for user...');
      
      const orgName = currentUser.email?.split('@')[0] || 'My Organization';
      const { organizationService } = getServices();
      const newOrg = await organizationService.createOrganization({
        name: orgName,
        email: currentUser.email || '',
        createdBy: currentUser.uid,
      });

      if (newOrg) {
        // Create membership for the user
        const { organizationMembershipService } = getServices();
        await organizationMembershipService.createMembership({
          userId: currentUser.uid,
          organizationId: newOrg.id,
          role: 'owner',
          isPrimary: true,
        });

        setOrganization(newOrg);
        
        // Reload memberships
        await loadUserMemberships(currentUser.uid);
      }

      return newOrg;
    } catch (error) {
      log.error('🏢 Org: Error creating default organization:', error);
      setError('Failed to create default organization');
      return null;
    }
  }, [currentUser, loadUserMemberships]);

  /**
   * Load organization members
   */
  const loadMembers = useCallback(async () => {
    if (!organization || !currentUser) {
      log.debug('🏢 Org: No organization or user, skipping member load');
      return;
    }

    try {
      const { organizationService } = getServices();
      const membersList = await organizationService.getOrganizationMembers(organization.id);
      setMembers(membersList);
    } catch (error) {
      log.error('🏢 Org: Error loading members:', error);
      setError('Failed to load organization members');
    }
  }, [organization, currentUser]);

  /**
   * Load organization data with loading guard
   */
  const loadOrganization = useCallback(
    async (id?: string) => {
      // Prevent concurrent loads
      if (loadingOrganizationRef.current) {
        log.debug('🏢 Org: Skipping duplicate organization load');
        return;
      }

      loadingOrganizationRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        if (!currentUser && !id) {
          log.debug('🏢 Org: No user or ID provided, clearing organization state');
          setOrganization(null);
          setMembers([]);
          return;
        }

        let org: Organization | null = null;

        if (id) {
          // Load organization by ID
          const { organizationService } = getServices();
          org = await organizationService.getOrganizationById(id);
        } else if (currentUser && userMemberships.length > 0) {
          // Use primary membership or first available
          const primaryMembership = userMemberships.find(m => m.isPrimary) || userMemberships[0];
          const { organizationService } = getServices();
          org = await organizationService.getOrganizationById(primaryMembership.organizationId);
        }

        setOrganization(org);

        if (org && currentUser) {
          // Load organization members only if user is authenticated
          await loadMembers();
          const { logEvent } = getServices();
          logEvent('view_organization', { organizationId: org.id });
        }
      } catch (err) {
        log.error('🏢 Org: Error loading organization:', err);
        setError('Failed to load organization data');
      } finally {
        setIsLoading(false);
        loadingOrganizationRef.current = false;
      }
    },
    [currentUser, userMemberships, loadMembers],
  );

  /**
   * Set active organization / Switch organization
   */
  const setActiveOrganization = async (organizationId: string) => {
    // Prevent recursive calls
    if (switchingOrganizationRef.current) {
      log.debug('🏢 Org: Already switching organization, skipping duplicate call');
      return;
    }
    
    try {
      switchingOrganizationRef.current = true;
      
      if (!currentUser) {
        log.debug('🏢 Org: No authenticated user, cannot set active organization');
        return;
      }

      // Load the new organization
      await loadOrganization(organizationId);

      // Update primary membership
      const currentPrimary = userMemberships.find(m => m.isPrimary);
      if (!currentPrimary || currentPrimary.organizationId !== organizationId) {
        const { organizationMembershipService } = getServices();
        await organizationMembershipService.setPrimaryMembership(currentUser.uid, organizationId);
        // Reload memberships to reflect the change
        await loadUserMemberships(currentUser.uid);
      }
      
      const { logEvent } = getServices();
      logEvent('switch_organization', { organizationId });
    } catch (err) {
      log.error('🏢 Org: Error setting active organization:', err);
      setError('Failed to set active organization');
      throw err;
    } finally {
      switchingOrganizationRef.current = false;
    }
  };

  // Alias for backward compatibility
  const switchOrganization = setActiveOrganization;

  /**
   * Refresh organization data
   */
  const refreshOrganization = async () => {
    if (organization) {
      await loadOrganization(organization.id);
    }
  };

  /**
   * Ensure user has organization access
   */
  const ensureOrganizationAccess = async (): Promise<boolean> => {
    if (!currentUser) {
      log.debug('🏢 Org: No user, cannot ensure organization access');
      return false;
    }

    try {
      // Check if user has any memberships
      if (userMemberships.length === 0) {
        await loadUserMemberships(currentUser.uid);
      }

      // If still no memberships, create default organization
      if (userMemberships.length === 0) {
        const newOrg = await createDefaultOrganization();
        return !!newOrg;
      }

      // If no organization loaded, load the primary one
      if (!organization) {
        const primaryMembership = userMemberships.find(m => m.isPrimary) || userMemberships[0];
        await loadOrganization(primaryMembership.organizationId);
      }

      return true;
    } catch (error) {
      log.error('🏢 Org: Error ensuring organization access:', error);
      return false;
    }
  };

  /**
   * Update organization data
   */
  const updateOrganization = async (
    data: Partial<OrganizationFormData>,
  ): Promise<Organization | null> => {
    if (!organization) {
      throw new Error('No active organization to update');
    }

    try {
      const { organizationService } = getServices();
      const updated = await organizationService.updateOrganization(organization.id, data);
      if (updated) {
        setOrganization(updated);
      }
      return updated;
    } catch (error) {
      log.error('🏢 Org: Error updating organization:', error);
      setError('Failed to update organization');
      throw error;
    }
  };

  /**
   * Remove member from organization
   */
  const removeMember = async (userId: string) => {
    if (!organization) {
      throw new Error('No active organization');
    }

    try {
      const { organizationService } = getServices();
      await organizationService.removeMember(organization.id, userId);
      await loadMembers();
    } catch (error) {
      log.error('🏢 Org: Error removing member:', error);
      throw error;
    }
  };

  // Load memberships and organization when user changes with proper debouncing
  useEffect(() => {
    // Clear any pending initialization timeout
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }
    
    const initializeOrganization = async () => {
      if (currentUser && !initializationRef.current) {
        try {
          // Wait for auth to be fully initialized
          await initManager.waitForPhase('AUTH');
          await initManager.waitForPhase('USER_PROFILE');
          
          // Mark initialization as in progress
          initializationRef.current = true;
          
          log.debug('🏢 Org: User authenticated, loading memberships for:', currentUser.uid);
          await loadUserMemberships(currentUser.uid);
          
          // Mark organization phase as complete
          initManager.markComplete('ORGANIZATION');
          isInitializedRef.current = true;
        } catch (error) {
          log.error('🏢 Org: Initialization error:', error);
          initManager.markFailed('ORGANIZATION', error as Error);
        }
      }
    };
    
    if (currentUser && !isInitializedRef.current) {
      // Debounce initialization by 100ms to let auth state settle
      initTimeoutRef.current = setTimeout(() => {
        initializeOrganization();
      }, 100);
    } else if (!currentUser) {
      log.debug('🏢 Org: No user, clearing organization state');
      initializationRef.current = false;
      isInitializedRef.current = false;
      setOrganization(null);
      setUserMemberships([]);
      setMembers([]);
      setIsLoading(false);
      lastUserIdRef.current = null;
    }
    
    // Cleanup function
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, [currentUser?.uid]); // Remove loadUserMemberships to prevent circular dependency

  const value = {
    organization,
    activeOrganization: organization, // Alias for backward compatibility
    userMemberships,
    isLoading,
    error,
    members,
    setActiveOrganization,
    switchOrganization,
    refreshOrganization,
    createDefaultOrganization,
    ensureOrganizationAccess,
    updateOrganization,
    loadOrganization,
    loadMembers,
    removeMember,
    getUserOrganizations,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

/**
 * HOC to require organization access
 */
export const withOrganization = <P extends object>(
  Component: React.ComponentType<P>,
): React.FC<P> => {
  return (props: P) => {
    const { organization, ensureOrganizationAccess } = useOrganization();
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
      const checkAccess = async () => {
        await ensureOrganizationAccess();
        setIsReady(true);
      };
      checkAccess();
    }, [ensureOrganizationAccess]);

    if (!isReady || !organization) {
      return <div>Loading organization...</div>;
    }

    return <Component {...props} />;
  };
};